import * as React from 'react';
import SortableTableHead, { HeadCell, Order } from '../../Utilities/SortableTableHead';
import ActivityViewmodel from '../Viewmodels/ActivityViewmodel';
import Paper from '@mui/material/Paper';
import Grid from '@mui/material/Grid';
import Typography from '@mui/material/Typography';
import TextField from '@mui/material/TextField';
import Button from '@mui/material/Button';
import TableContainer from '@mui/material/TableContainer';
import ResponsiveTable from '../../Utilities/ResponsiveTable';
import TableCell from '@mui/material/TableCell';
import TableBody from '@mui/material/TableBody';
import TableRow from '@mui/material/TableRow';
import CircularProgress from '@mui/material/CircularProgress';
import TablePagination from '@mui/material/TablePagination';
import authService from '../../api-authorization/AuthorizeService';
import { ResponseType, TokenRefreshContext } from '../../Contexts/TokenRefreshContext';
import ProjectActivityTableRow from './ProjectActivityTableRow';
import Collapse from '@mui/material/Collapse';
import IconButton from '@mui/material/IconButton';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import CancelIcon from '@mui/icons-material/Cancel';

interface IProps {
    projectId?: number;
}

export default function ProjectActivityTable(props: IProps) {
    const { projectId } = props;
    const { crabFetch } = React.useContext(TokenRefreshContext);
    const [order, setOrder] = React.useState<Order>('desc');
    const [orderBy, setOrderBy] = React.useState<keyof ActivityViewmodel>('date');
    const [page, setPage] = React.useState(0);
    const [rowsPerPage, setRowsPerPage] = React.useState(5);
    const [results, setResults] = React.useState<ActivityViewmodel[]>([]);
    const [count, setCount] = React.useState(0);
    const [search, setSearch] = React.useState('');
    const [loading, setLoading] = React.useState(false);
    const [collapsed, setCollapsed] = React.useState(false);
    const [headCells, setHeadCells] = React.useState<HeadCell<ActivityViewmodel>[]>([
        { id: 'description', property: 'Description', align: "left", disablePadding: false, label: 'Description' },
        { id: 'actionedBy', property: 'ActionedBy', align: "left", disablePadding: false, label: 'Actioned By' },
        { id: 'date', property: 'Date', align: "right", disablePadding: false, label: 'Date/Time' },
    ]);

    React.useEffect(() => {
        if (projectId) {
            setHeadCells([
                { id: 'description', property: 'Description', align: "left", disablePadding: false, label: 'Description' },
                { id: 'actionedBy', property: 'ActionedBy', align: "left", disablePadding: false, label: 'Actioned By' },
                { id: 'date', property: 'Date', align: "right", disablePadding: false, label: 'Date/Time' },
            ]);
        } else {
            setHeadCells([
                { id: 'description', property: 'Description', align: "left", disablePadding: false, label: 'Description' },
                { id: 'project', property: 'Project', align: "left", disablePadding: false, label: 'Project' },
                { id: 'actionedBy', property: 'ActionedBy', align: "left", disablePadding: false, label: 'Actioned By' },
                { id: 'date', property: 'Date', align: "right", disablePadding: false, label: 'Date/Time' },
            ]);
        }
    }, [projectId]);

    React.useEffect(() => {
        getData();
    }, [page, rowsPerPage, order, orderBy]);

    const getData = async () => {
        setLoading(true);
        const token = await authService.getAccessToken();
        const orderByProp = headCells.find(f => f.id === orderBy)!.property;

        await crabFetch(`Project/GetProjectActivity?orderBy=${orderByProp}&order=${order}&page=${page}&pageRows=${rowsPerPage}&search=${search}&id=${projectId ? projectId : ''}`, {
            headers: !token ? { 'Content-Type': 'application/json; charset=utf-8' } : { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json; charset=utf-8' },
        }, ResponseType.JSON,
            (data: any) => {
                setResults(data.rows);
                setCount(data.count);
                setLoading(false);
            },
            () => {
                setLoading(false);
            }
        );
    }

    const clearSearch = async () => {
        setLoading(true);
        const token = await authService.getAccessToken();
        const orderByProp = headCells.find(f => f.id === orderBy)!.property;

        await crabFetch(`Project/GetProjectActivity?orderBy=${orderByProp}&order=${order}&page=${page}&pageRows=${rowsPerPage}&search=${''}&id=${projectId ? projectId : ''}`, {
            headers: !token ? { 'Content-Type': 'application/json; charset=utf-8' } : { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json; charset=utf-8' },
        }, ResponseType.JSON,
            (data: any) => {
                setResults(data.rows);
                setCount(data.count);
                setLoading(false);
            },
            () => {
                setLoading(false);
            }
        );
    }

    const handleRequestSort = (event: React.MouseEvent<unknown>, property: keyof ActivityViewmodel) => {
        const isAsc = orderBy === property && order === 'asc';
        setOrder(isAsc ? 'desc' : 'asc');
        setOrderBy(property);
    };

    const handleChangePage = (event: unknown, newPage: number) => {
        setPage(newPage);
    };

    const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
        setRowsPerPage(parseInt(event.target.value, 10));
        setPage(0);
    };

    const handleSearch = (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        setSearch(event.target.value);
        if (page === 0 && event.target.value.length === 0) {
            setPage(0);
            clearSearch();
        }
    }

    const searchData = () => {
        if (page === 0) {
            getData();
        }
        setPage(0);
    }

    const keyPress = (event: any) => {
        if (event.keyCode === 13) {
            getData();
        }
    }

    const collapseSection = () => {
        setCollapsed(!collapsed);
    }

    const cancelSearch = () => {
        setSearch('');
        setPage(0);
        clearSearch();
    }

    const emptyRows = page > 0 ? Math.max(0, rowsPerPage - results.length) : 0;

    return (
        <Paper>
            <Grid container spacing={2}>
                <Grid item xs={12}>
                    <Grid container alignItems="center" justifyContent="space-between">
                        <Grid container item xs={'auto'} spacing={2}>
                            {projectId !== undefined &&
                                <Grid item>
                                    <IconButton onClick={collapseSection} size="small">{collapsed ? <ExpandMoreIcon /> : <ExpandLessIcon />}</IconButton>
                                </Grid>
                            }
                            <Grid item sx={{ display: 'flex' }} alignItems="center">
                                <Typography variant="h1">{projectId ? 'Project Activity' : 'User Activity'}</Typography>
                            </Grid>
                        </Grid>
                        {projectId === undefined &&
                            <Grid item>
                                <Grid container spacing={1} alignItems="center">
                                    <Grid item>
                                        <TextField
                                            id="outlined-search"
                                            type="text"
                                            color="primary"
                                            size="small"
                                            onChange={handleSearch}
                                            value={search}
                                            placeholder="Search..."
                                            onKeyDown={keyPress}
                                            InputProps={{
                                                endAdornment: search && (
                                                    <IconButton
                                                        size="small"
                                                        aria-label="clear search"
                                                        onClick={() => cancelSearch()}
                                                    >
                                                        <CancelIcon fontSize="small" />
                                                    </IconButton>
                                                )
                                            }}
                                        />
                                    </Grid>
                                    <Grid item>
                                        <Button aria-label={"Search Activity Log"} onClick={searchData} variant="contained">Search</Button>
                                    </Grid>
                                </Grid>
                            </Grid>
                        }
                    </Grid>
                </Grid>
                <Grid item xs={12}>
                    <Collapse in={!collapsed || projectId === undefined}>
                        <TableContainer>
                            <ResponsiveTable
                                aria-labelledby="tableTitle"
                                size={'medium'}
                                aria-label="activity log"
                            >
                                <SortableTableHead
                                    order={order}
                                    orderBy={orderBy}
                                    onRequestSort={handleRequestSort}
                                    headCells={headCells}
                                />
                                <TableBody>
                                    {(!loading && results) && results.map((row: ActivityViewmodel) => {
                                        return (<ProjectActivityTableRow key={row.id} row={row} projectId={projectId} />);
                                    })
                                    }
                                    {count <= 0 && !loading &&
                                        <TableRow
                                            sx={{
                                                height: 53 * rowsPerPage,
                                            }}
                                        >
                                            <TableCell colSpan={headCells.length + 1} align="center">No Results Found</TableCell>
                                        </TableRow>
                                    }
                                    {count > 0 && emptyRows > 0 && (
                                        <TableRow
                                            style={{
                                                height: 53 * emptyRows,
                                            }}
                                        >
                                            <TableCell colSpan={headCells.length + 1} />
                                        </TableRow>
                                    )}
                                    {loading &&
                                        <TableRow
                                            sx={{
                                                height: 53 * rowsPerPage,
                                            }}
                                        >
                                            <TableCell colSpan={headCells.length + 1} align="center"><CircularProgress color="primary" /></TableCell>
                                        </TableRow>
                                    }
                                </TableBody>
                            </ResponsiveTable>
                        </TableContainer>
                        <Grid container justifyContent="space-between" alignItems="center">
                            <Grid item>
                                <TablePagination
                                    rowsPerPageOptions={[5, 10, 25]}
                                    component="div"
                                    count={count}
                                    rowsPerPage={rowsPerPage}
                                    page={page}
                                    onPageChange={handleChangePage}
                                    onRowsPerPageChange={handleChangeRowsPerPage}
                                />
                            </Grid>
                        </Grid>
                    </Collapse>
                </Grid>
            </Grid>
        </Paper>
    );
}